
import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, Save, Download, Upload, ChevronDown, Plus, Trash2, AlertCircle, FileText, LayoutGrid, CheckCircle, RefreshCw, ArrowLeft, LogOut, Sun, Moon, Layers, Workflow } from 'lucide-react';
import { SimulationState, ProcessDefinition, AppNode } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface TopBarProps {
  simulationState: SimulationState;
  onToggleSimulation: () => void;
  onReset: () => void;
  
  // File Operations
  onExport: () => void;
  onImport: (file: File) => void;
  
  // Host Callbacks
  onSave?: () => void;
  onSaveAndQuit?: () => void;
  onBack?: () => void;
  
  // Multi-process props
  processes: ProcessDefinition[];
  activeProcessId: string;
  onSelectProcess: (id: string) => void;
  onCreateProcess: () => void;
  onCreateSubProcess?: () => void;
  onDeleteProcess: (id: string) => void;
  currentNodes: AppNode[]; // To check for live errors
  
  // Auto Layout
  onAutoLayout: () => void;

  // Auto Save
  autoSaveState?: { isSaving: boolean; lastSaved: Date | null };
}

const TopBar: React.FC<TopBarProps> = ({ 
  simulationState, 
  onToggleSimulation, 
  onReset, 
  onExport, 
  onImport,
  onSave,
  onSaveAndQuit,
  onBack,
  processes,
  activeProcessId,
  onSelectProcess,
  onCreateProcess,
  onCreateSubProcess,
  onDeleteProcess,
  currentNodes,
  onAutoLayout,
  autoSaveState
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme, isControlled } = useTheme();

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImport(file);
    }
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTheme = (e: React.MouseEvent) => {
    if (isControlled) return;
    
    const newTheme = theme === "light" ? "dark" : "light";

    // @ts-ignore - View Transition API
    if (!document.startViewTransition) {
        setTheme(newTheme);
        return;
    }

    const x = e.clientX;
    const y = e.clientY;
    const endRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
    );

    // @ts-ignore
    const transition = document.startViewTransition(() => {
        setTheme(newTheme);
    });

    transition.ready.then(() => {
        const clipPath = [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
        ];
        
        document.documentElement.animate(
            {
                clipPath: clipPath,
            },
            {
                duration: 500,
                easing: "ease-in-out",
                // The new view is always growing on top
                pseudoElement: "::view-transition-new(root)",
            }
        );
    });
  };

  const hasErrors = (nodes: AppNode[] | undefined) => {
      if (!nodes || !Array.isArray(nodes)) return false;
      return nodes.some(n => n?.data?.status === 'error');
  };

  const activeProcess = processes.find(p => p.id === activeProcessId);
  const activeProcessName = activeProcess?.name || 'Unknown Process';
  const activeProcessHasErrors = hasErrors(currentNodes);
  const isActiveSubProcess = !!activeProcess?.isSubProcess;

  return (
    <header className="h-[64px] bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 shadow-sm z-50">
      
      {/* Branding & Process Selector */}
      <div className="flex items-center gap-4">
        {onBack && (
            <button 
                onClick={onBack}
                className="p-2 -ml-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg transition-colors"
                title="Go Back"
            >
                <ArrowLeft size={20} />
            </button>
        )}

        <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center text-white">
                <FileText size={18} />
            </div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white leading-tight tracking-tight hidden md:block">Peak Editor</h1>
        </div>

        <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-2"></div>

        {/* Process Dropdown */}
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all min-w-[200px] justify-between
                ${activeProcessHasErrors 
                    ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' 
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-teal-500 hover:bg-slate-50'}`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {isActiveSubProcess ? <Layers size={14} className="text-slate-400 shrink-0"/> : <Workflow size={14} className="text-slate-400 shrink-0"/>}
                    <span className="text-sm font-semibold truncate max-w-[150px]">{activeProcessName}</span>
                    {isActiveSubProcess && (
                        <span className="text-[9px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">SUB</span>
                    )}
                    {activeProcessHasErrors && <AlertCircle size={14} className="text-red-500 shrink-0" />}
                </div>
                <ChevronDown size={14} className={`text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''} shrink-0`} />
            </button>

            {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl py-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Process Definitions
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {processes.map(proc => {
                            const isActive = proc.id === activeProcessId;
                            // Check errors: use live nodes if active, stored nodes if inactive
                            const procHasErrors = isActive ? activeProcessHasErrors : hasErrors(proc.nodes);
                            const isSub = !!proc.isSubProcess;
                            
                            return (
                                <div 
                                    key={proc.id}
                                    className={`group flex items-center justify-between px-3 py-2 mx-2 rounded-lg cursor-pointer transition-colors
                                    ${isActive ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                    onClick={() => {
                                        onSelectProcess(proc.id);
                                        setIsDropdownOpen(false);
                                    }}
                                >
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        {procHasErrors ? (
                                            <AlertCircle size={14} className="text-red-500 shrink-0" />
                                        ) : (
                                            isSub ? (
                                                <Layers size={14} className={`${isActive ? 'text-teal-500' : 'text-slate-400'} shrink-0`} />
                                            ) : (
                                                <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${isActive ? 'border-teal-500' : 'border-slate-300'}`}>
                                                    {isActive && <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />}
                                                </div>
                                            )
                                        )}
                                        <span className={`text-sm truncate ${isActive ? 'text-teal-900 dark:text-teal-100 font-medium' : 'text-slate-600 dark:text-slate-400'}`}>
                                            {proc.name}
                                        </span>
                                        {isSub && (
                                            <span className="text-[9px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 shrink-0">SUB</span>
                                        )}
                                    </div>

                                    {processes.length > 1 && (
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteProcess(proc.id);
                                            }}
                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                                            title="Delete Process"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="border-t border-slate-100 dark:border-slate-800 mt-2 pt-2 px-2 grid grid-cols-2 gap-2">
                        <button 
                            onClick={() => {
                                onCreateProcess();
                                setIsDropdownOpen(false);
                            }}
                            className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-slate-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/10 dark:text-slate-300 dark:hover:text-teal-400 rounded-lg transition-colors font-bold border border-slate-200 dark:border-slate-700"
                        >
                            <Plus size={12} />
                            Process
                        </button>
                        {onCreateSubProcess && (
                            <button 
                                onClick={() => {
                                    onCreateSubProcess();
                                    setIsDropdownOpen(false);
                                }}
                                className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-slate-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/10 dark:text-slate-300 dark:hover:text-teal-400 rounded-lg transition-colors font-bold border border-slate-200 dark:border-slate-700"
                            >
                                <Layers size={12} />
                                Sub-Process
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
      </div>

      {/* Simulation Controls */}
      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-800">
        <button
          onClick={onToggleSimulation}
          className={`
            flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all
            ${simulationState.isRunning 
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200' 
              : 'bg-white text-teal-700 hover:bg-teal-50 hover:text-teal-800 shadow-sm border border-slate-200'}
          `}
        >
          {simulationState.isRunning ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
          {simulationState.isRunning ? 'Pause' : 'Run Sim'}
        </button>

        <button 
          onClick={onReset}
          className="p-2 text-slate-500 hover:text-teal-600 hover:bg-white rounded-md transition-colors"
          title="Reset Simulation"
        >
          <RotateCcw size={16} />
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept=".bpjs,.json,.bpmn,.xml"
            onChange={handleFileChange} 
        />
        
        {/* Autosaved Indicator */}
        {autoSaveState && (
            <div className="hidden xl:flex items-center gap-2 mr-2">
                 {autoSaveState.isSaving ? (
                     <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono">
                         <RefreshCw size={12} className="animate-spin" />
                         SAVING...
                     </div>
                 ) : autoSaveState.lastSaved ? (
                     <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md font-medium" title={`Saved at ${autoSaveState.lastSaved.toLocaleTimeString()}`}>
                         <CheckCircle size={12} />
                         SAVED
                     </div>
                 ) : null}
            </div>
        )}

        {/* Theme Switcher - Hidden if Controlled */}
        {!isControlled && (
            <button
                onClick={toggleTheme}
                className="flex items-center justify-center p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:text-teal-400 dark:hover:bg-slate-900 rounded-lg transition-colors"
                title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}
            >
                {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>
        )}
        
        {/* Auto Layout Button */}
        <button 
            onClick={onAutoLayout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-teal-600 font-medium hover:bg-teal-50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-teal-400 rounded-lg transition-colors"
            title="Auto Layout Nodes"
        >
            <LayoutGrid size={16} />
            <span className="hidden lg:inline">Layout</span>
        </button>

        <button onClick={handleImportClick} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-teal-600 font-medium hover:bg-teal-50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-teal-400 rounded-lg transition-colors">
          <Upload size={16} />
          <span className="hidden lg:inline">Import</span>
        </button>
        <button onClick={onExport} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-teal-600 font-medium hover:bg-teal-50 dark:hover:bg-slate-900 dark:text-slate-400 dark:hover:text-teal-400 rounded-lg transition-colors">
          <Download size={16} />
          <span className="hidden lg:inline">Export</span>
        </button>
        
        {/* Host Actions */}
        {onSave && (
            <button 
                onClick={onSave}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm shadow-teal-600/20"
            >
              <Save size={16} />
              <span className="hidden lg:inline">Save</span>
            </button>
        )}

        {onSaveAndQuit && (
            <button 
                onClick={onSaveAndQuit}
                className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
            >
              <LogOut size={16} />
              <span className="hidden lg:inline">Save & Quit</span>
            </button>
        )}
      </div>
    </header>
  );
};

export default TopBar;